import React, { ReactNode, useContext, useState, useEffect, useMemo } from 'react';
import {
    TimeConversions,
    VESTED_SPOOL_CLAIMED,
    VESTED_SPOOL_PENDING,
} from '../../../config/constants';
import modalContext from '../../../context/modal/modalContext';
import Button from '../../../components/Button';
import themeContext from '../../../context/theme/themeContext';
import Cover from '../../../layout/Cover';
import { Contract, BigNumber } from 'ethers';
import { config } from '../../../config/config';
import abis from '../../../config/abis';
import { useWeb3React } from '@web3-react/core';
import { formatEther, parseEther } from '@ethersproject/units';
import { formatUSD } from '../../../helpers/formats';
import moment from 'moment';
import errorContext from '../../../context/error/errorContext';
import { sleep } from '../../../web3/utils';
import vestingAddresses from '../../../config/constants/vestingAddresses.json';

const VESTING_ENDING_AFTER_BURN = 1706665075;
const VESTING_ENDING_BEFORE_BURN = 1727703475;
const JANUARY_31ST_TO_SEPT_30TH = 243 * TimeConversions.SECONDS_PER_DAY;

const TIME_REMAINING =
    (VESTING_ENDING_AFTER_BURN - moment().unix()) / TimeConversions.SECONDS_PER_DAY;

const DaoVesting: React.FC<ReactNode> = () => {
    const { openModal, closeModal, setModalData } = useContext(modalContext);
    const { setError } = useContext(errorContext);

    const [claimableVestingAmount, setClaimableVestingAmount] = useState<BigNumber>(
        parseEther('0')
    );
    const [userVestAmount, setUserVestAmount] = useState<BigNumber>(parseEther('0'));

    const { theme } = useContext(themeContext);

    const { library, account } = useWeb3React();

    const isBuilderAccount = useMemo(
        () => vestingAddresses.builderAddress.toLowerCase() === account?.toLowerCase(),
        [account]
    );

    const vestingContract = useMemo(
        () =>
            new Contract(config.smartContract.vesting, abis.vesting, library.getSigner()),
        [library]
    );

    const builderVestingContract = useMemo(
        () =>
            new Contract(
                config.smartContract.builderVesting,
                abis.vesting,
                library.getSigner()
            ),
        [library]
    );

    useEffect(() => {
        (async () => {
            try {
                if (isBuilderAccount) {
                    const availableSpool = await builderVestingContract.getClaim();
                    setClaimableVestingAmount(availableSpool);

                    const vestAmount = await builderVestingContract.userVest(account);
                    setUserVestAmount(vestAmount[0].sub(availableSpool));
                    return;
                }

                const availableSpool = await vestingContract.getClaim();
                setClaimableVestingAmount(availableSpool);

                const vestAmount = await vestingContract.userVest(account);
                const timeRemainingBeforeBurn =
                    VESTING_ENDING_BEFORE_BURN - moment().unix();
                const timeRemainingAfterBurn =
                    timeRemainingBeforeBurn - JANUARY_31ST_TO_SEPT_30TH;
                const burnPercentage = timeRemainingAfterBurn / timeRemainingBeforeBurn;

                setUserVestAmount(
                    vestAmount[0]
                        .sub(availableSpool)
                        .mul(parseEther(burnPercentage.toString()))
                        .div(parseEther('1'))
                );
            } catch (error) {
                console.error(error);
            }
        })();
    }, [isBuilderAccount, account, library, vestingContract, builderVestingContract]);

    const claim = async () => {
        try {
            setModalData((prev) => ({ ...prev, message: VESTED_SPOOL_PENDING }));
            openModal('waitingModal');

            const claimVesting = await vestingContract.claim();

            const receipt = await claimVesting.wait();

            await sleep(5000);

            setModalData((prev) => ({
                ...prev,
                status: VESTED_SPOOL_CLAIMED,
                txHash: receipt.transactionHash,
                message: '',
            }));
            openModal('successModal');
        } catch (error) {
            setError({ code: -4002 });
        } finally {
            closeModal('waitingModal');
        }
    };

    const builderClaim = async () => {
        try {
            setModalData((prev) => ({ ...prev, message: VESTED_SPOOL_PENDING }));
            openModal('waitingModal');

            const claimVesting = await builderVestingContract.claim();

            const receipt = await claimVesting.wait();

            await sleep(5000);

            setModalData((prev) => ({
                ...prev,
                status: VESTED_SPOOL_CLAIMED,
                txHash: receipt.transactionHash,
                message: '',
            }));
            openModal('successModal');
        } catch (error) {
            setError({ code: -4002 });
        } finally {
            closeModal('waitingModal');
        }
    };

    return (
        <div className={`vault-tab dao-vesting ${theme}`}>
            <Cover useMinHeight={true}>
                <div className='inner__left'>
                    <h2>Spool DAO {isBuilderAccount && 'Builder'} Vesting Contract</h2>
                </div>
                <div className='inner__right'>
                    <p>
                        Thank you for being an essential part of Spool DAO! Your tokens
                        are vested linearly over 1.33 years (changed from 2 years after
                        the burn SIP-1.35) on a block-by-block basis starting from the 1st
                        of October 2022. Your voSPOOL tokens will decrease by the same
                        amount of $SPOOL you claim.
                    </p>
                    <p>Come back anytime to claim more $SPOOL.</p>
                    <div className='claimable-details'>
                        <div className='amount-claimable'>
                            <div className='text'>$SPOOL Available to Claim</div>
                            <div className='amount'>
                                {formatUSD(formatEther(claimableVestingAmount || '0'))}{' '}
                                $SPOOL
                            </div>
                        </div>
                        <div className='time-remaining'>
                            <div className='text'>
                                Remaining $SPOOL to be Vested over{' '}
                                {TIME_REMAINING < 0 ? 0 : Math.trunc(TIME_REMAINING)} Days
                            </div>
                            <div className='amount'>
                                {formatUSD(formatEther(userVestAmount || '0'))} $SPOOL
                            </div>
                        </div>
                        <Button
                            disabled={
                                claimableVestingAmount
                                    ? claimableVestingAmount.isZero()
                                    : true
                            }
                            onClick={isBuilderAccount ? builderClaim : claim}
                        >
                            Claim $SPOOL
                        </Button>
                    </div>
                </div>
            </Cover>
        </div>
    );
};

export default DaoVesting;
